const Keyboard = require('PSKeyboardController');
const State = require('PSState');
const PSConfig = require('PSConfig');
const i18n = require('i18n');
const { GameStates } = require('ClientStates');
const GenericEngine = require('GenericEngine');
const SlotUI = require('../GameUI/SlotUI');
const PSUIFeatures = require('./PSUIFeatures');
const Events = require('../../game-core/Generic/PSEvents');
const internalFlags = {
    gameActionActive: false,
    isBetLocked: false,
    isAutoplayLocked: false,
    isFreeRound: false,
    showFreeRoundEndDialog: false,
    slamStopDisabled: false,
};

let gameUI;
let autoplayButton;
let autoplayController;
let header;
let spinButton;
let autoplayPreventingStates;

const registerAutoplayListeners = () => {
    autoplayController.node.on('startAutoplay', (autoplaySpinRemaining) => {
        close();
        State.set({ autoplaySpinRemaining });
        if (internalFlags.slamStopDisabled) {
            spinButton.disableButton();
        } else {
            spinButton.setSlamStopState();
        }
        autoplayButton.setAutoplayState();
        autoplayButton.updateSpinAmount(autoplaySpinRemaining);
        Events.publish(Events.AUTOPLAY_START, { autoSpins: autoplaySpinRemaining });
        performSpin();
    });
};

const checkMaxLossWinConditions = () => {
    let result = false;

    if (autoplayController.hasMaxLossMet()) {
        result = true;
        gameUI.showMaxLossPopup();
        autoplayButton.stopAutoplay();
    }
    if (autoplayController.hasMaxWinMet()) {
        result = true;
        gameUI.showMaxWinPopup();
        autoplayButton.stopAutoplay();
    }
    return result;
};

const performSpin = () => {
    switch (State.gameState) {
        case GameStates.MAIN:
            const fundsCheckNotPassed = !SlotUI.isFeatureLocked() && !internalFlags.isFreeRound && !State.checkPlayerFunds();
            if (fundsCheckNotPassed) { // when we don't have enough balance to proceed the spin
                if (autoplayController.isAutoplay()) {
                    autoplayButton.stopAutoplay();
                }
                spinButton.enableButton();
                openFundsPopup();
            } else if (!autoplayController.isAutoplay()) { // check if we are not in autoplay, performe spin
                Events.publish(Events.SPIN_REQUEST, { betAmount: State.betAmount });
            } else {
                spinButton.access().then(() => {
                    // check again if during timeout the user didn't stop autoplay
                    if (autoplayController.isAutoplay()) {
                        Events.publish(Events.SPIN_REQUEST, { betAmount: State.betAmount });
                        spinButton.delayLock();
                    }
                });
            }
            break;
        default:
            Events.publish(Events.SPIN_REQUEST, { betAmount: State.betAmount });
            break;
    }
};

export const initConfig = (config) => {
    autoplayPreventingStates = config.autoplayPreventingStates;
    if (config.disableSkip) {
        internalFlags.slamStopDisabled = true;
    }

    gameUI.initConfig(config);

    autoplayController = gameUI.autoplayController;
    registerAutoplayListeners();
};

export const initState = (state) => {
    gameUI.initState(state);
    gameUI.showWin(state.betWinning);

    Events.subscribe(Events.STATE_UPDATED, (state) => { gameUI.updateState(state); });

    initButtonState(state);
};

const initButtonState = (state) => {
    if (SlotUI.isFeatureLocked()) {
        internalFlags.isAutoplayLocked = true;
        internalFlags.isBetLocked = true;
    } else if (internalFlags.isFreeRound) {
        internalFlags.isBetLocked = true;
    } else {
        internalFlags.isAutoplayLocked = false;
        internalFlags.isBetLocked = false;
    }

    switch (state.gameState) {
        case GameStates.MAIN:
            if (SlotUI.isFeatureLocked()) {
                disable([PSUIFeatures.AUTOPLAY, PSUIFeatures.BET]);
            } else if (internalFlags.isFreeRound) {
                disable([PSUIFeatures.BET]);
                enable([PSUIFeatures.AUTOPLAY]);
            } else {
                enable([PSUIFeatures.AUTOPLAY, PSUIFeatures.BET]);
            }
            break;
        case GameStates.FREESPINS:
        case GameStates.FREESPINS_SUPER:
            disable([PSUIFeatures.BET, PSUIFeatures.AUTOPLAY]);
            internalFlags.isAutoplayLocked = true;
            internalFlags.isBetLocked = true;
            break;
        default:
            disable([PSUIFeatures.BET, PSUIFeatures.AUTOPLAY]);
            break;
    }
}

export const externalSpinRequest = () => {
    if (autoplayController.isAutoplay() ||
        internalFlags.gameActionActive ||
        internalFlags.isFreeRound ||
        State.gameState !== GameStates.MAIN) {
        return;
    }

    Events.publish(Events.SPIN_REQUEST, { betAmount: State.betAmount });
};

export const initReplay = () => {
    disable([PSUIFeatures.BET, PSUIFeatures.AUTOPLAY]);
    gameUI.startReplay();
    gameUI.close();
};
export const startReplay = () => {
    gameUI.startReplay();
    gameUI.close();
    performSpin();
    Keyboard.unlock('replay');
};
export const stopReplay = () => {

    gameUI.stopReplay();
    spinButton.setDefaultHandler();
    spinButton.enableButton();
    Keyboard.unlock('replay');

    internalFlags.isBetLocked = internalFlags.isFreeRound;
    internalFlags.isAutoplayLocked = false;

    enable([PSUIFeatures.AUTOPLAY]);
    if (!internalFlags.isBetLocked) {
        enable([PSUIFeatures.BET]);
    }
};

export const startRestore = () => {
    performSpin();
    Keyboard.unlock('winRestore');
};

export const isPaused = () => {
    return gameUI.menu.isPaused;
}

const handleSpinRequest = () => {
    disable([PSUIFeatures.BET, PSUIFeatures.AUTOPLAY]);
    internalFlags.gameActionActive = true;

    if (State.quickSpinFeature || internalFlags.slamStopDisabled) {
        spinButton.disableButton();
    } else {
        spinButton.setSlamStopState();
    }

    switch (State.gameState) {
        case GameStates.MAIN:
            gameUI.showWin(0); // reset the winning

            if (autoplayController.isAutoplay()) {
                if (!autoplayController.isInfinite()) {
                    State.autoplaySpinRemaining--;
                }

                autoplayButton.updateSpinAmount(State.autoplaySpinRemaining);
                Events.publish(Events.AUTOPLAY_UPDATED, { remainingSpins: State.autoplaySpinRemaining });

                if (!autoplayController.isAutoplay()) {
                    // set flag to open popup after round ends
                    autoplayButton.stopAutoplay();
                }
            }
            break;
        case GameStates.RETRIGGERING:
            break;
        case GameStates.FREESPINS:
        case GameStates.FREESPINS_SUPER:
            internalFlags.isAutoplayLocked = true;
            internalFlags.isBetLocked = true;
            break;
    }
}

const registerKeyboardListeners = () => {
    Keyboard.registerUIHandlers(['numpad +', '='], () => { gameUI.betChanger.incButtonClick(); });
    Keyboard.registerUIHandlers(['-', 'numpad -'], () => { gameUI.betChanger.decButtonClick(); });
    Keyboard.registerUIHandlers(['esc'], () => {
        if (!State.menuEnabled) {
            return;
        }
        gameUI.escapeKey();
    });
}

const registerGameListeners = () => {
    // Events.STATE_UPDATED subscribe is done on initState to avoid event order issue
    Events.subscribe(Events.COMMON_INITIALIZED, () => {
        const data = GenericEngine.getInitData().freeRoundsData;
        if (data) {
            if (data.type === 'UNSPECIFIED') {
                Events.publish(Events.FREE_ROUNDS_UNSPECIFIED);
            } else {
                switch (data.status) {
                    case 'PENDING':
                        showFreeRoundsPendingDialog();
                        break;
                    case 'ACTIVE':
                        showFreeRoundsActivationDialog();
                        break;
                    case 'FINISHED':
                        break;
                }
            }
        }

        if (SlotUI.isReplayMode()) {
            Keyboard.lock('replay');
        }

        if (SlotUI.isWinRestore()) {
            Keyboard.lock('winRestore');
        }
    });
    Events.subscribe(Events.GAME_ROUND_ENDED, (data) => {
        if (internalFlags.isFreeRound) {
            if (!data.freeRoundsData) {
                internalFlags.isFreeRound = false;
                if (autoplayController.isAutoplay()) {
                    autoplayButton.stopAutoplay();
                }
            } else if (data.freeRoundsData.config && data.freeRoundsData.config.betAmount) {
                // Handles bet change where there are multiple free rounds sessions awarded.
                State.set({ betAmount: data.freeRoundsData.config.betAmount });
            }
        }

        internalFlags.gameActionActive = false;
        internalFlags.isBetLocked = internalFlags.isFreeRound || SlotUI.isReplayMode(); //release lock
        internalFlags.isAutoplayLocked = SlotUI.isReplayMode(); //release lock if no replay mode

        if (!autoplayController.isAutoplay()) {
            if (!internalFlags.isAutoplayLocked) {
                enable([PSUIFeatures.AUTOPLAY]);
            }
            if (!internalFlags.isBetLocked) {
                enable([PSUIFeatures.BET]);
            }

            let enableSpin = !internalFlags.isBetLocked && !internalFlags.isAutoplayLocked;

            enableSpin = enableSpin || internalFlags.isFreeRound && !internalFlags.isAutoplayLocked;
            enableSpin = enableSpin || !SlotUI.isReplayMode(); // if win or replay mode is passive, then we enable it to play
            if (enableSpin) {
                spinButton.enableButton();
            }
        } else if (State.gameState === GameStates.MAIN && autoplayController.isAutoplay() && !checkMaxLossWinConditions()) {
            performSpin();
        }
    });
    Events.subscribe(Events.FREE_ROUNDS_UNSPECIFIED, () => {
        internalFlags.isFreeRound = true;
        internalFlags.isBetLocked = true;
        disable([PSUIFeatures.BET]);
    });
    Events.subscribe(Events.FREE_ROUNDS_START, () => {
        internalFlags.isFreeRound = true;
        internalFlags.isBetLocked = true;
        header.show();
        header.updateFSRemaining(State.freeRoundsData);
        disable([PSUIFeatures.BET]);
    });
    Events.subscribe(Events.FREE_ROUNDS_PROGRESS, () => {
        internalFlags.isFreeRound = true;
        header.updateFSRemaining(State.freeRoundsData);
    });
    Events.subscribe(Events.FREE_ROUNDS_END, () => {
        internalFlags.isFreeRound = false;
        internalFlags.isBetLocked = SlotUI.isFeatureLocked();
        internalFlags.showFreeRoundEndDialog = true;
    });
    Events.subscribe(Events.SPIN_REQUEST, () => { handleSpinRequest(); });
    Events.subscribe(Events.REALITY_CHECK, (data) => { open('realityCheck', data) });
    Events.subscribe(Events.ENGINE_BALANCE_UPDATE, () => { gameUI.updateBalanceLabel(State.balance); });

    Events.subscribe(Events.AUTOPLAY_ENDED, () => {
        if (!internalFlags.gameActionActive) {
            if (!internalFlags.isAutoplayLocked) {
                enable([PSUIFeatures.AUTOPLAY]);
            }
            if (!internalFlags.isBetLocked) {
                enable([PSUIFeatures.BET]);
            }
            spinButton.enableButton();
        }
        autoplayController.reset();
        State.set({ autoplaySpinRemaining: 0 });
    });

    Events.subscribe(Events.ENGINE_CUSTOM_MESSAGE, ({ type, data }) => {
        switch (type) {
            case 'WinRestoreDialogResponse': // YGGDRASIL
                initButtonState(State.get());

                if (data.restoreMode === 'SKIP') {
                    if (data.winnings && data.winnings > 0) {
                        State.betWinning = data.winnings;
                        gameUI.showWin(State.betWinning);
                    }
                    Keyboard.unlock('winRestore');
                }
                Events.publish(Events.WIN_RESTORE_RESPONSE, data);
                break;
            case 'BoostTopBarDisplayed':
                if (!isNaN(data.height)) {
                    const top = {
                        boostBar: data.height
                    };
                    State.updateGameInnerCanvasSize({ top });
                }
                break;
            case 'BoostTopBarRemoved':
                State.updateGameInnerCanvasSize({ top: { boostBar: 0 } });
                break;
            default:
                cc.log(`Events.ENGINE_CUSTOM_MESSAGE: ${type}`, data);
                break;
        }
    });
}

const registerButtonsListeners = () => {
    // SPIN BUTTON PRESSED
    spinButton.node.on(Events.BUTTON_SPIN, () => {
        Events.publish(Events.BUTTON_SPIN);
        if (State.gameState === GameStates.MAIN) {
            spinButton.delayLock();
        }
        performSpin();
    });
    spinButton.node.on(Events.BUTTON_SLAMSTOP, () => { Events.publish(Events.BUTTON_SLAMSTOP); });
    spinButton.node.on(Events.BUTTON_FAST_POPUP, () => { });
    autoplayButton.node.on(Events.BUTTON_STOP_AUTOPLAY, () => { Events.publish(Events.AUTOPLAY_ENDED); });
    autoplayButton.node.on(Events.AUTOPLAY_OPEN, () => { gameUI.showAutoplay(); });
}

function registerUIListeners() {
    gameUI.node.on('logout', () => { logout(1); });
    gameUI.node.on('closeGame', () => { closeGame(); });
    gameUI.node.on('closed', () => {
        Keyboard.enableGameKeys();
        Events.publish(Events.MENU_CLOSED);
    });
    gameUI.node.on('opened', () => {
        Keyboard.disableGameKeys();
        Events.publish(Events.MENU_OPENED);
    });

    gameUI.node.on('historyRequest', () => { Events.publish(Events.ROUND_HISTORY_REQUEST, 0) });
}

export const init = (gameUIController) => {
    gameUI = gameUIController;
    spinButton = gameUI.spinButton;
    autoplayButton = gameUI.autoplayButton;
    autoplayController = gameUI.autoplayController;
    header = gameUI.header;

    registerKeyboardListeners();
    registerGameListeners();
    registerUIListeners();
    registerButtonsListeners();
};

export const roundAnimationEndRequest = () => {
    // replay mode handling done in the wrapper
    Events.publish(Events.ROUND_ANIMATION_ENDED);

    if (SlotUI.isReplayMode()) {
        disableSpinButton();
    }

    if (State.gameState !== GameStates.MAIN) {
        // FREESPINS CASCADES, CASCADES FREESPINS, RETRIGGERING
        performSpin();
    }
};

export const actionEndRequest = (dontShowWin) => {
    Events.publish(Events.GAME_ACTION_ANIMATION_ENDED, { balance: State.balance });

    if (autoplayPreventingStates.indexOf(State.gameState) >= 0 && autoplayController.isAutoplay()) {
        autoplayButton.stopAutoplay();
    }

    if (internalFlags.showFreeRoundEndDialog) {
        showFreeRoundsFinishedDialog();
        if (autoplayController.isAutoplay()) {
            autoplayButton.stopAutoplay();
        }
    }
    if (GenericEngine.getSessionData().maxWinExceeded) {
        showMaxWinCapExceededDialog();
    }
    // we assume that the balance updated is when spin request received from engine
    if (!dontShowWin) {
        gameUI.showWin(State.betWinning);
    }
};

export const notify = (titleKey, messageKey, actions = []) => {
    const { errorDialogsDisabled } = PSConfig.get();

    if (errorDialogsDisabled) {
        Events.publish(Events.ERROR_DIALOG, { message: titleKey });
        return;
    }

    const popupData = {
        title: titleKey,
        message: messageKey,
        closeEnabled: true,
        buttons: []
    };

    if (actions.includes('RELAUNCH') || actions.includes('RECONNECT')) {
        popupData.buttons.push({
            title: 'relaunch',
            handler: () => { location.reload() }
        });
        popupData.closeEnabled = false;
    }

    if (actions.includes('EXIT')) {
        popupData.buttons.push({
            title: 'leave-game',
            handler: () => { closeGame(); }
        });
        popupData.closeEnabled = false;
    }

    open('infoPopup', popupData);
};

export const showFreeRoundsPendingDialog = () => {
    let { betAmount, targetWin } = State.freeRoundsData.config;
    const { numLeft } = State.freeRoundsData.data;
    const { id, type } = State.freeRoundsData;
    betAmount = i18n.formatCurrency(betAmount);
    targetWin = i18n.formatCurrency(targetWin);
    const acceptHandler = () => {
        Events.publish(Events.FREE_ROUNDS_START);
        GenericEngine.activateFreeRound(id);
        close();
    };
    const rejectHandler = () => {
        if (!internalFlags.isAutoplayLocked) {
            enable([PSUIFeatures.AUTOPLAY]);
        }
        if (!internalFlags.isBetLocked) {
            enable([PSUIFeatures.BET]);
        }
        GenericEngine.rejectFreeRound(id)
        State.freeRoundsData = null;
        close();
    };

    disable([PSUIFeatures.BET]);

    let message;
    let messageOption;
    switch (type) {
        case 'NUM_ROUNDS':
            message = 'dialog-message-free-round-pending';
            messageOption = { '0': numLeft, '1': betAmount };
            break;
        case 'SPIN_WIN':
            message = 'dialog-message-free-round-single-win-pending';
            messageOption = { '0': targetWin };
            break;
        case 'TOTAL_WIN':
            message = 'dialog-message-free-round-total-win-pending';
            messageOption = { '0': targetWin };
            break;
    }

    const infoPopupData = {
        title: 'dialog-title-free-round-pending',
        message,
        messageOption,
        closeEnabled: false,
        buttons: [
            {
                title: 'reject',
                handler: rejectHandler
            },
            {
                title: 'accept',
                handler: acceptHandler
            }
        ]
    }
    open('infoPopup', infoPopupData);
};

export const showFreeRoundsActivationDialog = () => {
    let { betAmount, targetWin } = State.freeRoundsData.config;
    let { type } = State.freeRoundsData;
    const { numLeft } = State.freeRoundsData.data;

    betAmount = i18n.formatCurrency(betAmount);
    targetWin = i18n.formatCurrency(targetWin);
    let message;
    let messageOption;

    switch (type) {
        case 'NUM_ROUNDS':
            message = 'dialog-message-free-round-pending';
            messageOption = { '0': numLeft, '1': betAmount };
            break;
        case 'SPIN_WIN':
            message = 'dialog-message-free-round-single-win-pending';
            messageOption = { '0': targetWin };
            break;
        case 'TOTAL_WIN':
            message = 'dialog-message-free-round-total-win-pending';
            messageOption = { '0': targetWin };
            break;
    }

    const infoPopupData = {
        title: 'dialog-title-free-round-pending',
        message,
        messageOption,
        closeEnabled: true
    };
    open('infoPopup', infoPopupData);
    Events.publish(Events.FREE_ROUNDS_START);
};

export const showFreeRoundsFinishedDialog = () => {
    header.hide();

    internalFlags.showFreeRoundEndDialog = false;

    const infoPopupData = {
        title: 'dialog-title-free-round-finished',
        message: 'dialog-message-free-round-finished',
        closeEnabled: true
    };
    open('infoPopup', infoPopupData);
};

export const showMaxWinCapDialog = () => {
    const { maxWin } = PSConfig.get();

    const infoPopupData = {
        title: 'dialog-title-win-cap',
        message: 'dialog-message-win-cap',
        messageOption: { '0': maxWin },
        closeEnabled: true
    };
    open('infoPopup', infoPopupData);
};

export const showMaxWinCapExceededDialog = () => {
    const { maxWin } = PSConfig.get();

    const infoPopupData = {
        title: 'dialog-title-win-cap',
        message: 'dialog-message-win-cap-exceeded',
        messageOption: { '0': maxWin },
        closeEnabled: true
    };
    open('infoPopup', infoPopupData);
};

/**
 * Enables the CommonUI keyboard events
 * @param {boolean} enable Setting it to false will disable the keyboard.
 */
export const enableCUIKeyboard = (enable = true) => {
    if (enable === true) {
        Keyboard.enable();
    } else {
        Keyboard.disable();
    }
};

/**
 * Enables the CommonUI keyboard events
 * @param {boolean} enable Setting it to false will disable the handler for that specific key.
 * @param {string|string[]} keys Key or keys that will get their handlers enabled or disabled.
 */
export const enableCUIKeyboardKeys = (enable = true, keys = 'all') => {
    let handleKeys;

    // check if there is one key input or an array of keys and handle that appropriately
    if (keys === 'all') {
        handleKeys = Keyboard.getAllAvailableKeyCodes();
    } else if (typeof keys === 'string') {
        handleKeys = [keys];
    } else if (keys instanceof Array) {
        handleKeys = keys;
    }

    if (enable === true) {
        Keyboard.enableHandlers(handleKeys);
    } else {
        Keyboard.disableHandlers(handleKeys);
    }
};
/**
 * Enables the CommonUI keyboard events
 * @param {boolean} enable Setting it to false will disable the handler for that specific key.
 * @param {string|string[]} keys Key or keys that will get their handlers enabled or disabled.
 */
export const enableCUIKeyboardKeysOnly = (keys) => {
    let handleKeys;

    // check if there is one key input or an array of keys and handle that appropriately
    if (keys === 'all') {
        handleKeys = Keyboard.getAllAvailableKeyCodes();
    } else if (typeof keys === 'string') {
        handleKeys = [keys];
    } else if (keys instanceof Array) {
        handleKeys = keys;
    }

    Keyboard.enableHandlersOnly(handleKeys);
};

export const disableCUIKeyboardKeys = (keys = 'all') => {
    let handleKeys;

    // check if there is one key input or an array of keys and handle that appropriately
    if (keys === 'all') {
        handleKeys = Keyboard.getAllAvailableKeyCodes();
    } else if (typeof keys === 'string') {
        handleKeys = [keys];
    } else if (keys instanceof Array) {
        handleKeys = keys;
    }

    Keyboard.disableHandlers(handleKeys);
};


export const startGame = () => {
    enableCUIKeyboard();
    enableCUIKeyboardKeys();
    enableCUIKeyboard();
    showStatusBar();
    setDefaultSpinButtonHandler();

    if (SlotUI.isReplayMode() || SlotUI.isWinRestore()) {
        disableSpinButton();

        if (SlotUI.isReplayMode()) {
            startReplay();
        }

        if (SlotUI.isWinRestore()) {
            startRestore();
        }
    } else {
        enableSpinButton();
    }

    if (State.get().isExitReplay) {
        stopReplay();
        State.set({ isExitReplay: false }, 0);
    }

};

export const showStatusBar = () => {
    gameUI.showStatusBar();

    const { maxWin } = GenericEngine.getSessionData();
    if (maxWin > 0) {
        showMaxWinCapDialog();
    }

    Events.publish(Events.UI_ENABLED);
};

export const externalAutoplayStop = () => {
    if (autoplayController.isAutoplay()) {
        autoplayButton.stopAutoplay();
    }
}

const exForcePauseStatus = {
    paused: false,
    ccDirectorPaused: false,
    blocker: null
};

export const externalForcePause = () => {
    if (exForcePauseStatus.paused) {
        return;
    }

    exForcePauseStatus.paused = true;

    if (!cc.director.isPaused()) {
        cc.director.pause();
        exForcePauseStatus.ccDirectorPaused = true;
        Events.publish(Events.GAME_PAUSED);
    }

    Keyboard.lock('externalForcePause');

    if (!exForcePauseStatus.blocker) {
        const cover = new cc.Node('ForcePauseBlocker');

        cover.parent = cc.director.getScene();
        cover.name = 'ForcePauseBlocker';

        cover.addComponent(cc.BlockInputEvents);
        cover.width = 10000;
        cover.height = 10000;
        cover.zIndex = 10000;
        exForcePauseStatus.blocker = cover;
        cc.game.addPersistRootNode(cover);
    }
    exForcePauseStatus.blocker.zIndex = 10000;
    exForcePauseStatus.blocker.active = true;
}

export const externalForceResume = () => {
    if (!exForcePauseStatus.paused) {
        return;
    }

    exForcePauseStatus.paused = false;
    exForcePauseStatus.blocker.active = false;

    Keyboard.unlock('externalForcePause');

    if (exForcePauseStatus.ccDirectorPaused) {
        cc.director.resume();
        exForcePauseStatus.ccDirectorPaused = false;
        Events.publish(Events.GAME_RESUMED);
    }
}
const shouldTriggerClick = () => {
    return (SlotUI.isReplayMode() || SlotUI.isWinRestore()) &&
        (State.gameState === 'FREESPINS' ||
            State.gameState === 'FREESPINS_SUPER' ||
            State.gameState === 'FREESPINS_MEGA');
}

export const setSlamStopState = () => {
    if (!internalFlags.slamStopDisabled) {
        spinButton.setSlamStopState();
    }
};
export const closeGame = () => {
    const { clickHandler } = SlotUI.getMenuElement(SlotUI.MenuElements.Home);
    if (clickHandler) {
        clickHandler();
    }
    Events.publish(Events.CLOSE_GAME);
};
export const enableSpinButton = () => {
    spinButton.enableButton();
    if (shouldTriggerClick()) {
        spinButton.onClick();
    }
};
export const disableSpinButton = () => { spinButton.disableButton() };
export const setCustomSpinButtonHandler = (handler, keyboardEnabled) => {
    spinButton.setCustomHandler(handler, keyboardEnabled);
    if (shouldTriggerClick()) {
        spinButton.onClick();
    }
}
export const openFundsPopup = () => {
    const noFundsData = SlotUI.getMenuElement(SlotUI.MenuElements.NoFunds);
    switch (noFundsData.implementationType) {
        case SlotUI.UIComponentImplementationTypes.BUILTIN:
            open('infoPopup', {
                title: 'error-title-out-of-money',
                message: 'error-message-out-of-money',
                actions: ['CONTINUE']
            });
            Events.publish(Events.ERROR_OUT_OF_MONEY);
            break;
        case SlotUI.UIComponentImplementationTypes.PROVIDED:
            Events.publish(Events.SPIN_REQUEST, { betAmount: State.betAmount });
            break;

    }
}
export const isGameRoundActive = () => { return internalFlags.gameActionActive };
export const freeRoundFlag = () => { return internalFlags.isFreeRound };
export const isAutoplay = () => { return autoplayController.isAutoplay() };
export const freeSpinsEndRequest = () => { };
export const setDefaultSpinButtonHandler = () => { spinButton.setDefaultHandler(); }
export const logout = (reason) => { Events.publish(Events.LOGOUT, reason); };
export const close = () => { gameUI.close(); };
export const enable = (arr) => { gameUI.enable(arr); };
export const disable = (arr) => { gameUI.disable(arr); };
export const hideStatusBar = () => { gameUI.hideStatusBar(); };
export const open = (page, data) => { gameUI.open(page, data); };
export const freeSpinsStartRequest = () => { };
export const registerKeyboardHandler = (keys, handler) => { Keyboard.registerHandler(keys, handler) }
export const unregisterKeyboardHandler = (handler) => { Keyboard.unregisterHandler(handler) }
export const openRealityCheck = () => { open('realityCheck') };
export const registerComplianceBarDisplayedListener = () => { SlotUI.registerComplianceBarDisplayedListener(); }
